/**
 * 샵관리 — 매입 영수증 OCR
 * 영수증/거래명세서 사진 → (브라우저 내 tesseract.js) 글자 인식 → 품목 표로 정리 → 사람이 확인·수정 → 초안 저장.
 * 초안은 나중에 입고관리로 넘겨 재고에 쌓는다(shop/receiptStore.draftToStockInRows).
 * 첫 인식 때 한글 학습데이터(약 수 MB)를 내려받으므로 인터넷 연결이 필요하다.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import useTenantCode from 'hooks/useTenantCode';
import { PageContainer, ActionButton } from 'styles/common.styles';
import { showAlert } from 'utils/toastBridge';
import { loadTable, fmt, num, today } from 'shop/shopStore';
import { parseReceipt, matchProduct } from 'shop/receiptParser';
import { loadDrafts, saveDraft, removeDraft } from 'shop/receiptStore';

const SAMPLES = [
  { url: '/samples/shop-receipts/receipt-sample-1.png', label: '샘플 1 · 거래명세서' },
  { url: '/samples/shop-receipts/receipt-sample-2.png', label: '샘플 2 · 영수증' },
];

const Wrap = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 380px) 1fr;
  gap: 12px;
  align-items: start;
  font-size: 12px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
  .box { border: 1px solid #d9dde3; border-radius: 4px; background: #fff; padding: 8px 10px; display: flex; flex-direction: column; gap: 8px; }
  .title { font-weight: 700; font-size: 13px; }
  .row { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
  .hint { color: #5b6472; }
  .warn { color: #c0392b; }
  img.preview { max-width: 100%; border: 1px solid #e5e8ee; }
  input[type='text'], input[type='date'], input[type='number'], select {
    height: 24px; font-size: 12px; border: 1px solid #c5cad3; border-radius: 3px; padding: 0 4px;
  }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #d9dde3; padding: 2px 4px; }
  th { background: #f3f5f8; }
  td.n input { width: 84px; text-align: right; }
  tr.bad td { background: #fff3f1; }
  progress { width: 100%; }
  pre { max-height: 160px; overflow: auto; background: #f7f8fa; padding: 6px; white-space: pre-wrap; }
`;

const newKey = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

const ReceiptOcr = () => {
  const tc = useTenantCode();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ status: '', value: 0 });
  const [rawText, setRawText] = useState('');
  const [head, setHead] = useState({ receiptDate: today(), vendorNm: '', ocrTotal: 0 });
  const [lines, setLines] = useState([]);
  const [drafts, setDrafts] = useState(() => loadDrafts(tc));

  const products = useMemo(() => loadTable(tc, 'product').filter((p) => p.useYn !== 'N'), [tc]);
  const sum = lines.reduce((s, l) => s + num(l.amount), 0);

  useEffect(() => () => previewUrl && previewUrl.startsWith('blob:') && URL.revokeObjectURL(previewUrl), [previewUrl]);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setRawText('');
    setLines([]);
  };

  const pickSample = async (s) => {
    try {
      const blob = await (await fetch(s.url)).blob();
      pickFile(new File([blob], s.url.split('/').pop(), { type: blob.type || 'image/png' }));
    } catch (e) {
      showAlert('샘플 이미지를 불러오지 못했습니다.', 'error');
    }
  };

  const run = async () => {
    if (!file) return;
    setBusy(true);
    setProgress({ status: '엔진 준비 중', value: 0 });
    let worker;
    try {
      const { createWorker } = await import('tesseract.js');
      worker = await createWorker(['kor', 'eng'], 1, {
        logger: (m) => setProgress({ status: m.status, value: m.progress || 0 }),
      });
      const { data } = await worker.recognize(file);
      setRawText(data.text || '');
      const parsed = parseReceipt(data.text || '');
      setHead({ receiptDate: parsed.receiptDate || today(), vendorNm: parsed.vendorNm, ocrTotal: parsed.ocrTotal });
      setLines(parsed.lines.map((l) => ({ ...l, key: newKey(), productUid: matchProduct(l.name, products) })));
      showAlert(
        parsed.lines.length ? `품목 ${parsed.lines.length}건을 읽었습니다. 내용을 확인하세요.` : '품목을 찾지 못했습니다. 아래 인식 원문을 보고 직접 입력하세요.',
        parsed.lines.length ? 'success' : 'warning'
      );
    } catch (e) {
      showAlert(`인식 실패: ${e.message || e} (인터넷 연결을 확인하세요)`, 'error');
    } finally {
      if (worker) await worker.terminate();
      setBusy(false);
      setProgress({ status: '', value: 0 });
    }
  };

  const setLine = (key, patch) =>
    setLines((prev) => prev.map((l) => {
      if (l.key !== key) return l;
      const next = { ...l, ...patch };
      // 수량·단가를 고치면 금액 자동 계산 (금액을 직접 고친 경우는 그대로)
      if ('qty' in patch || 'unitPrice' in patch) next.amount = num(next.qty) * num(next.unitPrice);
      next.warn = num(next.qty) * num(next.unitPrice) !== num(next.amount);
      return next;
    }));

  const save = () => {
    const valid = lines.filter((l) => String(l.name).trim());
    if (!valid.length) return showAlert('저장할 품목이 없습니다.', 'warning');
    if (!head.receiptDate) return showAlert('거래일자를 입력하세요.', 'warning');
    saveDraft(tc, {
      receiptDate: head.receiptDate,
      vendorNm: head.vendorNm,
      imageName: file?.name || '',
      ocrTotal: head.ocrTotal,
      lines: valid.map(({ name, qty, unitPrice, amount, productUid }) => ({
        name: String(name).trim(), qty: num(qty), unitPrice: num(unitPrice), amount: num(amount), productUid,
      })),
    });
    setDrafts(loadDrafts(tc));
    setLines([]);
    setRawText('');
    return showAlert('초안으로 저장했습니다. (입고관리 반영은 연동 후)', 'success');
  };

  const del = (d) => {
    if (!window.confirm(`${d.receiptDate} ${d.vendorNm} 초안을 삭제할까요?`)) return;
    removeDraft(tc, d.draftUid);
    setDrafts(loadDrafts(tc));
  };

  return (
    <PageContainer>
      <Wrap>
        <div className="box">
          <div className="title">1. 영수증 이미지</div>
          <div className="row">
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={(e) => pickFile(e.target.files?.[0])} />
          </div>
          <div className="row">
            <span className="hint">샘플로 테스트:</span>
            {SAMPLES.map((s) => (
              <ActionButton key={s.url} type="button" onClick={() => pickSample(s)}>{s.label}</ActionButton>
            ))}
          </div>
          {previewUrl && <img className="preview" src={previewUrl} alt="영수증 미리보기" />}
          <div className="row">
            <ActionButton type="button" disabled={!file || busy} onClick={run}>{busy ? '인식 중…' : '🔍 글자 인식'}</ActionButton>
          </div>
          {busy && (
            <>
              <progress value={progress.value} max="1" />
              <span className="hint">{progress.status}</span>
            </>
          )}
          <span className="hint">첫 인식 때 한글 데이터를 내려받아 오래 걸릴 수 있습니다. 인식 결과는 틀릴 수 있으니 오른쪽에서 꼭 확인하세요.</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="box">
            <div className="title">2. 인식 결과 확인·수정</div>
            <div className="row">
              <label>거래일자 <input type="date" value={head.receiptDate} onChange={(e) => setHead({ ...head, receiptDate: e.target.value })} /></label>
              <label>공급처 <input type="text" style={{ width: 200 }} value={head.vendorNm} onChange={(e) => setHead({ ...head, vendorNm: e.target.value })} /></label>
            </div>
            <table>
              <thead>
                <tr><th>품명(영수증)</th><th>수량</th><th>단가</th><th>금액</th><th>제품 매칭</th><th /></tr>
              </thead>
              <tbody>
                {lines.length === 0 && <tr><td colSpan={6} className="hint">인식 결과가 여기에 표시됩니다. (직접 줄을 추가할 수도 있습니다)</td></tr>}
                {lines.map((l) => (
                  <tr key={l.key} className={l.warn ? 'bad' : ''}>
                    <td><input type="text" style={{ width: '100%' }} value={l.name} onChange={(e) => setLine(l.key, { name: e.target.value })} /></td>
                    <td className="n"><input type="number" style={{ width: 56 }} value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} /></td>
                    <td className="n"><input type="number" value={l.unitPrice} onChange={(e) => setLine(l.key, { unitPrice: e.target.value })} /></td>
                    <td className="n"><input type="number" value={l.amount} onChange={(e) => setLine(l.key, { amount: e.target.value })} /></td>
                    <td>
                      <select value={l.productUid} onChange={(e) => setLine(l.key, { productUid: e.target.value })}>
                        <option value="">(미매칭 — 신규 제품)</option>
                        {products.map((p) => <option key={p.productUid} value={p.productUid}>{p.productCode} {p.productNm}</option>)}
                      </select>
                    </td>
                    <td><ActionButton type="button" onClick={() => setLines(lines.filter((x) => x.key !== l.key))}>✕</ActionButton></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="row">
              <ActionButton type="button" onClick={() => setLines([...lines, { key: newKey(), name: '', qty: 1, unitPrice: 0, amount: 0, productUid: '', warn: false }])}>＋ 줄 추가</ActionButton>
              <span>품목 합계 <b>₩{fmt(sum)}</b></span>
              {head.ocrTotal > 0 && (
                <span className={head.ocrTotal !== sum ? 'warn' : 'hint'}>
                  영수증 합계 ₩{fmt(head.ocrTotal)} {head.ocrTotal !== sum ? '— 품목 합계와 다릅니다. 붉은 줄을 확인하세요' : '— 일치'}
                </span>
              )}
              <ActionButton type="button" onClick={save}>💾 초안 저장</ActionButton>
            </div>
            {rawText && (
              <details>
                <summary className="hint">인식 원문 보기</summary>
                <pre>{rawText}</pre>
              </details>
            )}
          </div>

          <div className="box">
            <div className="title">3. 저장된 초안 ({drafts.length})</div>
            <table>
              <thead><tr><th>거래일자</th><th>공급처</th><th>품목</th><th>금액</th><th>매칭</th><th>상태</th><th /></tr></thead>
              <tbody>
                {drafts.length === 0 && <tr><td colSpan={7} className="hint">저장된 초안이 없습니다.</td></tr>}
                {drafts.map((d) => (
                  <tr key={d.draftUid}>
                    <td>{d.receiptDate}</td>
                    <td>{d.vendorNm}</td>
                    <td>{d.lines.map((l) => l.name).join(', ')}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(d.lines.reduce((s, l) => s + num(l.amount), 0))}</td>
                    <td>{d.lines.filter((l) => l.productUid).length}/{d.lines.length}</td>
                    <td>{d.status === 'APPLIED' ? '입고 반영' : '대기'}</td>
                    <td>{d.status !== 'APPLIED' && <ActionButton type="button" onClick={() => del(d)}>삭제</ActionButton>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <span className="hint">초안은 입고관리 연동 시 재고 입고 행으로 변환됩니다(제품 매칭이 된 줄만).</span>
          </div>
        </div>
      </Wrap>
    </PageContainer>
  );
};

export default ReceiptOcr;
